import { useEffect, useState } from 'react';

import hooksConstants from 'src/constants/hooks.constants';
import { useAssignmentNotifications } from 'src/hooks/use-assignment-notifications.hook';
import { NormalizedRealtimePayload } from 'src/interfaces/api.types';
import {
  Assignment,
  AssignmentsViewOptions,
} from 'src/interfaces/assignment.types';
import { UserRoles } from 'src/interfaces/user-role.enum';
import authService from 'src/services/api/auth.service';
import {
  removeScheduledAssignmentsEvent,
  useActiveAssignmentsViewSelector,
  useActiveUserClassesSelector,
  useScheduledAssignmentsEventsSelector,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { isActivityDisplayed } from 'src/utils/assignments.utils';
import { dayjs } from 'src/utils/dayjs-config.utils';

import { useActiveAssignments } from './use-active-assignments';

const assignmentsUpdateInterval = hooksConstants.ASSIGNMENTS_UPDATE_INTERVAL; // a minute

const useAssignmentsRegularUpdate = () => {
  const isStudent = authService.getRole() === UserRoles.STUDENT;

  const selectedClass = useSelectedClassSelector();
  const activeUserClasses = useActiveUserClassesSelector();
  const activeAssignmentsView = useActiveAssignmentsViewSelector();
  const isToDoActive = activeAssignmentsView === AssignmentsViewOptions.TODO;

  const dispatch = useAppDispatch();

  const scheduledAssignmentsEvents = useScheduledAssignmentsEventsSelector();
  const { showNewAssignmentsNotification } = useAssignmentNotifications();

  const { refetch: refetchAssignments, data: assignments } =
    useActiveAssignments();

  const [shouldRefetchToDoAssignments, setShouldRefetchToDoAssignments] =
    useState(false);

  // returns events of scheduled assignments that have started within last minute
  const getRangeScheduledAssignmentsEvents = () => {
    const currentTime = dayjs();

    return scheduledAssignmentsEvents.filter((event) => {
      const assignmentStartDate = dayjs(event.startDate);
      const timeDifference = currentTime.diff(assignmentStartDate);

      return timeDifference > 0 && timeDifference <= assignmentsUpdateInterval; // in the range of a minute
    });
  };

  const getDisplayedActivitiesCountFromEvent = (
    event: NormalizedRealtimePayload,
    displayedAssignments: Assignment[],
  ) =>
    event.activitiesBks.reduce((count, activityBk) => {
      const isDisplayed = isActivityDisplayed(displayedAssignments, activityBk);

      if (isDisplayed) return count + 1;

      return count;
    }, 0);

  const handleScheduledAssignmentsNotifications = (
    assignments: Assignment[],
  ) => {
    const scheduledAssignmentEventsInOneMinute =
      getRangeScheduledAssignmentsEvents();
    const newActivitiesCount = scheduledAssignmentEventsInOneMinute.reduce(
      (count, event) => {
        const displayedEventActivitiesCount =
          getDisplayedActivitiesCountFromEvent(event, assignments);

        if (displayedEventActivitiesCount) {
          const newActivitiesCount = event.newActivitiesCount ?? 0;

          dispatch(removeScheduledAssignmentsEvent(event));

          return count + newActivitiesCount;
        }

        return count;
      },
      0,
    );

    if (isStudent && newActivitiesCount) {
      showNewAssignmentsNotification(newActivitiesCount);
    }
  };

  useEffect(() => {
    if (!assignments) {
      return;
    }

    const interval = setInterval(async () => {
      const scheduledAssignmentEventsWithinLastMinute =
        getRangeScheduledAssignmentsEvents();

      // prevent assignments re-fetch when there are no scheduled assignments started within last minute
      if (!scheduledAssignmentEventsWithinLastMinute.length) {
        return;
      }

      if (!isToDoActive) {
        showNewAssignmentsNotification(1);
        setShouldRefetchToDoAssignments(true);
      }

      const { data: currentAssignments } = await refetchAssignments();

      const notificationPayload = currentAssignments
        ? currentAssignments.assignments
        : [];

      handleScheduledAssignmentsNotifications(notificationPayload);
    }, assignmentsUpdateInterval);

    return () => {
      clearInterval(interval);
    };
    // Avoid including methods in the deps as they're redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedClass,
    activeUserClasses,
    scheduledAssignmentsEvents,
    assignments,
    isToDoActive,
  ]);

  useEffect(() => {
    if (!shouldRefetchToDoAssignments || !isToDoActive) return;

    refetchAssignments();
    setShouldRefetchToDoAssignments(false);
    // Avoid including the re-fetching method in the deps as it's redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetchToDoAssignments, isToDoActive]);
};

export default useAssignmentsRegularUpdate;
